class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.notification = document.getElementById('cart-notification');
    this.header = document.querySelector('sticky-header');
    this.onBodyClick = this.handleBodyClick.bind(this);

    this.notification.addEventListener(
      'keyup',
      (evt) => evt.code === 'Escape' && this.close()
    );

    this.querySelectorAll('button[type="button"]').forEach((closeButton) =>
      closeButton.addEventListener('click', this.close.bind(this))
    );

    this.notification.addEventListener(
      'click',
      this.handleRecommendationClick.bind(this)
    );
  }

  open() {
    this.notification.classList.add('animate', 'active');

    this.notification.addEventListener(
      'transitionend',
      () => {
        this.notification.focus();
        trapFocus(this.notification);
      },
      { once: true }
    );

    document.body.addEventListener('click', this.onBodyClick);
  }

  close() {
    this.notification.classList.remove('active');
    document.body.removeEventListener('click', this.onBodyClick);

    removeTrapFocus(this.activeElement);
  }

  renderContents(parsedState) {
    this.cartItemKey = parsedState.key;

    this.getSectionsToRender().forEach((section) => {
      const target = document.getElementById(section.id);
      const sectionHTML = parsedState.sections?.[section.id];

      if (!target || !sectionHTML) return;

      target.innerHTML = this.getSectionInnerHTML(
        sectionHTML,
        section.selector
      );
    });

    if (this.header) this.header.reveal();

    this.open();

    if (parsedState.product_id) {
      this.loadRecommendation(parsedState.product_id);
    } else {
      this.hideRecommendation();
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-notification-product',
        selector: `[id="cart-notification-product-${this.cartItemKey}"]`,
      },
      {
        id: 'cart-notification-button',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const parsedDocument = new DOMParser().parseFromString(
      html,
      'text/html'
    );

    const selectedElement = parsedDocument.querySelector(selector);

    return selectedElement ? selectedElement.innerHTML : '';
  }

  getRecommendationContainer() {
    return this.notification.querySelector(
      '#wonjo-cart-notification-recommendation'
    );
  }

  hideRecommendation() {
    const container = this.getRecommendationContainer();

    if (!container) return;

    container.hidden = true;
    container.innerHTML = '';
  }

  async fetchRecommendations(productId, intent) {
    const root = window.Shopify?.routes?.root || '/';

    const params = new URLSearchParams({
      product_id: String(productId),
      limit: '6',
      intent,
    });

    const response = await fetch(
      `${root}recommendations/products.json?${params.toString()}`,
      {
        headers: {
          Accept: 'application/json',
        },
      }
    );

    if (!response.ok) {
      throw new Error(
        `Recommendation request failed: ${response.status}`
      );
    }

    const data = await response.json();

    return Array.isArray(data.products) ? data.products : [];
  }

  async fetchCart() {
    const root = window.Shopify?.routes?.root || '/';

    const response = await fetch(`${root}cart.js`, {
      headers: {
        Accept: 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Cart request failed: ${response.status}`);
    }

    return response.json();
  }

findBestProduct(products, cartProductIds) {
  const eligibleProducts = products.filter((product) => {
    return (
      product.available &&
      !cartProductIds.has(String(product.id)) &&
      Array.isArray(product.variants) &&
      product.variants.some((variant) => variant.available)
    );
  });

  if (!eligibleProducts.length) return null;

  const quickAddProducts = eligibleProducts.filter((product) => {
    const availableVariants = product.variants.filter(
      (variant) => variant.available
    );

    return availableVariants.length === 1;
  });

  const preferredProducts = quickAddProducts.length
    ? quickAddProducts
    : eligibleProducts;

  const selectionPool = preferredProducts.slice(0, 4);

  return selectionPool[
    Math.floor(Math.random() * selectionPool.length)
  ];
}

  async loadRecommendation(productId) {
    const container = this.getRecommendationContainer();

    if (!container || !productId) return;

    container.hidden = true;
    container.innerHTML = '';

    try {
      const cart = await this.fetchCart();

      const cartProductIds = new Set(
        cart.items.map((item) => String(item.product_id))
      );

      let products = await this.fetchRecommendations(
        productId,
        'complementary'
      );

      let product = this.findBestProduct(
        products,
        cartProductIds
      );

      if (!product) {
        products = await this.fetchRecommendations(
          productId,
          'related'
        );

        product = this.findBestProduct(
          products,
          cartProductIds
        );
      }

      if (!product) {
        this.hideRecommendation();
        return;
      }

      this.renderRecommendation(product);
    } catch (error) {
      console.warn(
        'Wonjo cart notification recommendation could not load:',
        error
      );

      this.hideRecommendation();
    }
  }

  renderRecommendation(product) {
    const container = this.getRecommendationContainer();

    if (!container) return;

    const isKorean =
      document.documentElement.lang
        .toLowerCase()
        .startsWith('ko');

    const availableVariants = product.variants.filter(
      (variant) => variant.available
    );

    const canQuickAdd = availableVariants.length === 1;
    const selectedVariant = availableVariants[0];

    const image =
      product.featured_image ||
      product.images?.[0] ||
      '';

    const heading = isKorean
      ? '추천 상품'
      : 'You May Also Like';

    const addLabel = isKorean ? '+ 담기' : '+ Add';
    const addingLabel = isKorean ? '담는 중…' : 'Adding…';
    const optionsLabel = isKorean
      ? '옵션 선택'
      : 'Choose options';

    const price = this.formatPrice(
      selectedVariant?.price || product.price
    );

    const productURL = product.url || '#';

    const actionMarkup = canQuickAdd
      ? `
        <button
          type="button"
          class="wonjo-notification-recommendation__add"
          data-wonjo-recommendation-add
          data-variant-id="${selectedVariant.id}"
          data-default-label="${this.escapeHTML(addLabel)}"
          data-loading-label="${this.escapeHTML(addingLabel)}"
        >
          ${this.escapeHTML(addLabel)}
        </button>
      `
      : `
        <a
          href="${this.escapeHTML(productURL)}"
          class="wonjo-notification-recommendation__add"
        >
          ${this.escapeHTML(optionsLabel)}
        </a>
      `;

    container.innerHTML = `
      <div class="wonjo-notification-recommendation__heading">
        ${this.escapeHTML(heading)}
      </div>

      <div class="wonjo-notification-recommendation__product">
        <a
          href="${this.escapeHTML(productURL)}"
          class="wonjo-notification-recommendation__image-link"
          aria-label="${this.escapeHTML(product.title)}"
        >
          ${
            image
              ? `
                <img
                  src="${this.escapeHTML(image)}"
                  alt="${this.escapeHTML(product.title)}"
                  class="wonjo-notification-recommendation__image"
                  loading="lazy"
                  width="72"
                  height="72"
                >
              `
              : ''
          }
        </a>

        <div class="wonjo-notification-recommendation__details">
          <a
            href="${this.escapeHTML(productURL)}"
            class="wonjo-notification-recommendation__title"
          >
            ${this.escapeHTML(product.title)}
          </a>

          <div class="wonjo-notification-recommendation__bottom">
            <span class="wonjo-notification-recommendation__price">
              ${this.escapeHTML(price)}
            </span>

            ${actionMarkup}
          </div>
        </div>
      </div>
    `;

    container.hidden = false;
  }

formatPrice(price) {
  const currency = window.Shopify?.currency?.active || 'KRW';

  const locale = document.documentElement.lang
    .toLowerCase()
    .startsWith('ko')
    ? 'ko-KR'
    : 'en-US';

  const amount = Number(price || 0) / 100;

  const formatted = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    currencyDisplay: 'narrowSymbol',
    maximumFractionDigits: currency === 'KRW' ? 0 : 2,
  }).format(amount);

  return currency === 'KRW'
    ? `${formatted} KRW`
    : formatted;
}

  escapeHTML(value) {
    return String(value ?? '')
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');
  }

  async handleRecommendationClick(event) {
    const button = event.target.closest(
      '[data-wonjo-recommendation-add]'
    );

    if (!button) return;

    event.preventDefault();
    event.stopPropagation();

    if (button.disabled) return;

    const variantId = button.dataset.variantId;
    const defaultLabel =
      button.dataset.defaultLabel || '+ Add';
    const loadingLabel =
      button.dataset.loadingLabel || 'Adding…';

    if (!variantId) return;

    button.disabled = true;
    button.textContent = loadingLabel;

    const root = window.Shopify?.routes?.root || '/';

    try {
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({
          items: [
            {
              id: variantId,
              quantity: 1,
            },
          ],
          sections: this.getSectionsToRender().map(
            (section) => section.id
          ),
          sections_url: window.location.pathname,
        }),
      });

      const parsedState = await response.json();

      if (!response.ok || parsedState.status) {
        throw new Error(
          parsedState.description ||
            parsedState.message ||
            'Unable to add product'
        );
      }

      /*
       * cart/add.js returns an items array when using the items format.
       * renderContents expects the newly added line item properties.
       */
      const addedItem =
        parsedState.items?.[0] || parsedState;

      addedItem.sections = parsedState.sections;

      if (
        typeof publish === 'function' &&
        typeof PUB_SUB_EVENTS !== 'undefined'
      ) {
        await publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'wonjo-notification-recommendation',
          productVariantId: variantId,
          cartData: addedItem,
        });
      }

      this.renderContents(addedItem);
    } catch (error) {
      console.error(
        'Wonjo recommendation could not be added:',
        error
      );

      button.disabled = false;
      button.textContent = defaultLabel;
    }
  }

  handleBodyClick(evt) {
    const target = evt.target;

    if (
      target !== this.notification &&
      !target.closest('cart-notification')
    ) {
      const disclosure = target.closest(
        'details-disclosure, header-menu'
      );

      this.activeElement = disclosure
        ? disclosure.querySelector('summary')
        : null;

      this.close();
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define(
  'cart-notification',
  CartNotification
);